/**
 * Homepage Destinations section.
 *
 * - Fetches ALL callable destinations from `/api/rates/countries`.
 * - Shows 12 featured countries first; "View all destinations" expands to the
 *   full 60+ list.
 * - Sticky, real-time search filtering by country name, ISO code or dial code.
 *   Searching also auto-expands the full list so partial matches across the
 *   whole catalogue are surfaced.
 * - Mobile-first: 2-col grid on phones, search bar sticks to the top of the
 *   section as the user scrolls.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronDown, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/site/Reveal";
import { flagFor } from "@/components/customer/CountryPicker";
import { walletApi, gbp } from "@/lib/wallet";

// Featured set shown by default — the headline 12 markets.
const FEATURED = [
  "GB", "NG", "GH", "KE", "UG", "ZA", "US", "CA", "AU", "IN", "AE", "DE",
];

function RateCard({ c }) {
  return (
    <a
      href="#dialer"
      data-testid={`rate-card-${c.country_code}`}
      className="rounded-2xl bg-white border border-brand-line p-3 sm:p-4 hover:shadow-xl hover:-translate-y-0.5 hover:border-brand-green/40 transition-all"
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl leading-none" aria-hidden>{flagFor(c.country_code)}</span>
        <span className="font-mono text-[11px] sm:text-xs font-bold text-brand-muted">+{c.prefix}</span>
      </div>
      <p className="mt-1.5 sm:mt-2 font-semibold text-xs sm:text-sm text-brand-ink truncate">{c.country_name}</p>
      <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs text-brand-muted">
        from <span className="font-bold text-brand-greenDark">{gbp(c.rate_per_minute)}</span>/min
      </p>
    </a>
  );
}

export default function HomeDestinations() {
  const [items, setItems] = useState(null); // null = loading
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    walletApi.get("/rates/countries")
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]));
  }, []);

  // Searching auto-expands so partial matches across the full list are visible.
  const searching = q.trim().length > 0;

  const filtered = useMemo(() => {
    if (!items) return [];
    const needle = q.trim().toLowerCase().replace(/^\+/, "");
    if (!needle) return items;
    return items.filter((c) =>
      c.country_name.toLowerCase().includes(needle) ||
      c.country_code.toLowerCase().includes(needle) ||
      c.prefix.includes(needle),
    );
  }, [items, q]);

  const visible = useMemo(() => {
    if (searching) return filtered;
    if (expanded) return filtered;
    // Featured-first ordering when not searching.
    const byCode = Object.fromEntries(filtered.map((c) => [c.country_code, c]));
    return FEATURED.map((cc) => byCode[cc]).filter(Boolean);
  }, [filtered, expanded, searching]);

  return (
    <section className="bg-brand-bgSoft" data-testid="rates-section">
      <div className="container-page py-12 lg:py-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-6 lg:mb-8">
            <p className="label-eyebrow text-brand-green">Live rates</p>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-brand-ink leading-tight">
              Search 60+ destinations. <span className="text-brand-green">Per second.</span>
            </h2>
            <p className="mt-3 text-sm text-brand-muted">
              Every destination shown below is live in the dialer right now.
            </p>
          </div>
        </Reveal>

        {/* Sticky search */}
        <div className="sticky top-16 z-30 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 bg-brand-bgSoft/95 backdrop-blur" data-testid="rates-search-wrap">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              ref={searchRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Search country or dial code — e.g. "Nigeria" or "+234"'
              data-testid="rates-search"
              className="w-full rounded-full bg-white border border-brand-line shadow-sm pl-11 pr-11 py-3 text-sm focus:border-brand-green focus:ring-2 focus:ring-brand-green/10 focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); searchRef.current?.focus(); }}
                data-testid="rates-search-clear"
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 hover:bg-brand-bgSoft"
              >
                <X className="h-4 w-4 text-brand-muted" />
              </button>
            )}
          </div>
          {searching && (
            <p className="mt-2 text-center text-[11px] text-brand-muted" data-testid="rates-search-count">
              {filtered.length === 0
                ? "No destinations found"
                : `${filtered.length} ${filtered.length === 1 ? "destination" : "destinations"} match “${q}”`}
            </p>
          )}
        </div>

        {/* Cards grid */}
        <div className="mt-6">
          {items === null ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" aria-busy>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={`skel-${i}`} className="h-24 sm:h-28 rounded-2xl bg-white border border-brand-line animate-pulse" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12" data-testid="rates-empty">
              <p className="font-display font-black text-lg text-brand-ink">No destinations found</p>
              <p className="mt-1 text-sm text-brand-muted">
                Try a different country name or dial code. As we add new rates they’ll appear here automatically.
              </p>
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="rates-empty-clear"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-2 text-xs font-bold"
              >
                Reset search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="rates-preview-grid">
              {visible.map((c) => <RateCard key={c.country_code} c={c} />)}
            </div>
          )}
        </div>

        {/* CTAs */}
        {items && items.length > 0 && !searching && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#dialer" data-testid="rates-start-calling"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-6 py-3 text-sm font-bold shadow-[0_12px_24px_-8px_rgba(31,139,58,0.6)]">
              <Phone className="h-4 w-4 fill-current" /> Start Calling
            </a>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="rates-view-all"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-brand-line bg-white hover:bg-white text-brand-ink px-5 py-3 text-sm font-bold"
            >
              {expanded ? "Show featured only" : `View all ${items.length} destinations`}
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            <Link to="/rates" data-testid="rates-section-all"
              className="text-sm font-bold text-brand-greenDark hover:text-brand-greenHover">
              See full price list →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
